import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaBed, FaDoorOpen, FaExclamationTriangle, FaHospital, FaSearch, FaUsers } from "react-icons/fa";
import { supabase } from "../../../supabaseClient";
import Header from "../../../components/IntranetHeader";
import Footer from "../../../components/Footer";

function pressure(total, occupied, unavailable) {
  const usable = Math.max(0, total - unavailable);
  const ratio = usable ? occupied / usable : 1;
  if (!usable || ratio >= .95) return { label: "Critical", className: "critical" };
  if (ratio >= .8) return { label: "High", className: "high" };
  if (ratio >= .6) return { label: "Moderate", className: "moderate" };
  return { label: "Normal", className: "normal" };
}

export default function Wards({ session, profile }) {
  const navigate = useNavigate();
  const [beds, setBeds] = useState([]);
  const [settings, setSettings] = useState([]);
  const [staffAssignments, setStaffAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("All");
  const [showCreate, setShowCreate] = useState(false);
  const [newWard, setNewWard] = useState({ ward: "", display_name: "", specialty: "", floor: "" });
  const [feedback, setFeedback] = useState("");

  useEffect(() => {
    let active = true;
    async function load() {
      setLoading(true);
      const [bedResult, settingsResult, staffResult] = await Promise.all([
        supabase.from("beds").select("id, ward, patient_id, status, bed_number"),
        supabase.from("ward_settings").select("*"),
        supabase.from("ward_staff_assignments").select("id, ward, profile_id"),
      ]);
      if (!active) return;
      setBeds(bedResult.data || []);
      setSettings(settingsResult.data || []);
      setStaffAssignments(staffResult.data || []);
      setLoading(false);
    }
    void load();
    return () => { active = false; };
  }, []);

  const wards = useMemo(() => {
    const names = new Set([...beds.map((bed) => bed.ward), ...settings.map((item) => item.ward)].filter(Boolean));
    return [...names].map((ward) => {
      const wardBeds = beds.filter((bed) => bed.ward === ward);
      const cfg = settings.find((item) => item.ward === ward) || {};
      const occupied = wardBeds.filter((bed) => bed.patient_id).length;
      const unavailable = wardBeds.filter((bed) => ["Cleaning", "Closed"].includes(bed.status)).length;
      const available = Math.max(0, wardBeds.length - occupied - unavailable);
      return {
        ward,
        displayName: cfg.display_name || ward,
        specialty: cfg.specialty || "General inpatient care",
        floor: cfg.floor || "Location not set",
        operationalStatus: cfg.operational_status || "Open",
        total: wardBeds.length,
        occupied,
        unavailable,
        available,
        staff: staffAssignments.filter((item) => item.ward === ward).length,
        pressure: pressure(wardBeds.length, occupied, unavailable),
      };
    }).sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [beds, settings, staffAssignments]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return wards.filter((ward) => {
      const matchesSearch = !q || [ward.displayName, ward.ward, ward.specialty, ward.floor].some((value) => String(value || "").toLowerCase().includes(q));
      const matchesStatus = status === "All" || ward.operationalStatus === status;
      return matchesSearch && matchesStatus;
    });
  }, [wards, search, status]);

  const totals = useMemo(() => ({
    wards: wards.length,
    beds: wards.reduce((sum, ward) => sum + ward.total, 0),
    occupied: wards.reduce((sum, ward) => sum + ward.occupied, 0),
    available: wards.reduce((sum, ward) => sum + ward.available, 0),
    pressured: wards.filter((ward) => ["High", "Critical"].includes(ward.pressure.label)).length,
  }), [wards]);

  async function createWard() {
    const wardCode = newWard.ward.trim();
    if (!wardCode) { setFeedback("Enter a ward name or code first."); return; }
    const payload = { ward: wardCode, display_name: newWard.display_name.trim() || wardCode, specialty: newWard.specialty.trim(), floor: newWard.floor.trim(), operational_status: "Open", updated_by: session?.user?.id || null };
    const { error } = await supabase.from("ward_settings").insert(payload);
    if (error) { setFeedback(error.message || "Ward could not be created."); return; }
    navigate(`/intranet/wards/${encodeURIComponent(wardCode)}`);
  }

  return (
    <div className="flex flex-col min-h-screen pguh-wards-page">
      <Header session={session} profile={profile} />
      <main className="flex-1">
        <div className="nhsuk-width-container nhsuk-u-margin-top-6 nhsuk-u-margin-bottom-8">
          <header className="pguh-wards-hero">
            <div><span className="pguh-eyebrow">Clinical operations</span><h1>Ward command centre</h1><p>Live bed capacity, staffing and operational pressure across inpatient wards.</p></div>
            <div className="pguh-wards-hero__actions"><button type="button" className="nhsuk-button nhsuk-button--secondary" onClick={() => setShowCreate((value) => !value)}>{showCreate ? "Cancel" : "Create ward"}</button><FaHospital aria-hidden="true" /></div>
          </header>
          {feedback && <div className="nhsuk-inset-text">{feedback}</div>}
          {showCreate && <section className="pguh-ward-create-panel"><div><span className="pguh-eyebrow">New ward</span><h2>Create ward</h2><p>Create the ward shell first, then add beds and staffing from its workspace.</p></div><div className="pguh-ward-create-form"><label><span>Ward name / code</span><input className="nhsuk-input" value={newWard.ward} onChange={(e)=>setNewWard((v)=>({...v,ward:e.target.value}))} placeholder="e.g. Ward 4A"/></label><label><span>Display name</span><input className="nhsuk-input" value={newWard.display_name} onChange={(e)=>setNewWard((v)=>({...v,display_name:e.target.value}))} placeholder="e.g. Acute Medical Unit"/></label><label><span>Specialty</span><input className="nhsuk-input" value={newWard.specialty} onChange={(e)=>setNewWard((v)=>({...v,specialty:e.target.value}))}/></label><label><span>Floor / location</span><input className="nhsuk-input" value={newWard.floor} onChange={(e)=>setNewWard((v)=>({...v,floor:e.target.value}))}/></label><button className="nhsuk-button" type="button" onClick={createWard}>Create and open ward</button></div></section>}

          <section className="pguh-wards-summary" aria-label="Ward overview">
            <div><FaHospital/><span>Wards</span><strong>{totals.wards}</strong></div>
            <div><FaBed/><span>Total beds</span><strong>{totals.beds}</strong></div>
            <div><FaUsers/><span>Occupied</span><strong>{totals.occupied}</strong></div>
            <div><FaDoorOpen/><span>Available</span><strong>{totals.available}</strong></div>
            <div className={totals.pressured ? "is-warning" : ""}><FaExclamationTriangle/><span>High pressure</span><strong>{totals.pressured}</strong></div>
          </section>

          <section className="pguh-wards-toolbar">
            <label><span><FaSearch/> Search wards</span><input className="nhsuk-input" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Ward, specialty or floor" /></label>
            <label><span>Operational status</span><select className="nhsuk-select" value={status} onChange={(event) => setStatus(event.target.value)}><option>All</option><option>Open</option><option>Restricted</option><option>Closed</option></select></label>
          </section>

          {loading ? <p>Loading ward information…</p> : filtered.length === 0 ? <div className="pguh-wards-empty"><h2>No wards found</h2><p>No configured ward matches the current filters.</p></div> : (
            <div className="pguh-wards-grid">
              {filtered.map((ward) => {
                const usable = Math.max(0, ward.total - ward.unavailable);
                const ratio = usable ? Math.min(100, (ward.occupied / usable) * 100) : 100;
                return <article className="pguh-ward-overview-card" key={ward.ward}>
                  <div className="pguh-ward-overview-card__top">
                    <div><span className={`pguh-ward-operational is-${ward.operationalStatus.toLowerCase()}`}>{ward.operationalStatus}</span><h2>{ward.displayName}</h2><p>{ward.specialty}</p></div>
                    <span className={`pguh-ward-pressure is-${ward.pressure.className}`}>{ward.pressure.label} pressure</span>
                  </div>
                  <div className="pguh-ward-overview-card__meta"><span>{ward.floor}</span><span>{ward.staff} staff assigned</span></div>
                  <div className="pguh-ward-capacity"><div><strong>{ward.occupied}</strong><span>Occupied</span></div><div><strong>{ward.available}</strong><span>Available</span></div><div><strong>{ward.unavailable}</strong><span>Unavailable</span></div><div><strong>{ward.total}</strong><span>Total</span></div></div>
                  <div className="pguh-ward-capacity-bar"><span style={{ width: `${ratio}%` }} /></div>
                  <div className="pguh-ward-overview-card__footer"><small>{Math.round(ratio)}% of usable beds occupied</small><button className="nhsuk-button nhsuk-button--secondary" type="button" onClick={() => navigate(`/intranet/wards/${encodeURIComponent(ward.ward)}`)}>Open ward</button></div>
                </article>;
              })}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
